import { BareActionContext, getStoreBuilder } from "vuex-typex";
import * as Models from "../models";
import { RootState } from "@/store";
import * as TranslationService from "@/services/Translations";
import { configuratorConstants } from "../models";
import Cookie from "@/services/Cookie";

export interface ProductConfiguratorState {
    configuratorType: string;
    products: Models.Product[];
    productListModules: Models.ProductListModuleModel[];
    propertyGroups: Models.ConfiguratorPropertyGroup[];
    configuratorValues: Models.ConfiguratorModuleValue[];
    propertyModelValues: Models.PropertyModelValue[];
    translations: Models.TranslationModel;
    apiUrl: string;
    stepByStepInstructionsProducts: Models.Product[];
}

const initialState: ProductConfiguratorState = {
    configuratorType: "",
    products: [],
    productListModules: [],
    propertyGroups: [],
    configuratorValues: [],
    propertyModelValues: [],
    apiUrl: "/api/productconfigurator",
    translations: TranslationService.getTranslations<Models.TranslationModel>(),
    stepByStepInstructionsProducts: [],
};

const store = getStoreBuilder<RootState>().module("productConfigurator", initialState);

function products(state: ProductConfiguratorState) {
    return state.products;
}

function productListModules(state: ProductConfiguratorState) {
    return state.productListModules;
}

function configuratorPropertyGroups(state: ProductConfiguratorState) {
    return state.propertyGroups;
}

function configuratorValues(state: ProductConfiguratorState) {
    return state.configuratorValues;
}

function propertyModelValues(state: ProductConfiguratorState) {
    return state.propertyModelValues;
}

function apiUrl(state: ProductConfiguratorState) {
    return state.apiUrl;
}

function translations(state: ProductConfiguratorState) {
    return state.translations;
}

function configuratorType(state: ProductConfiguratorState) {
    return state.configuratorType;
}

function stepByStepInstructionsProducts(state: ProductConfiguratorState) {
    return state.stepByStepInstructionsProducts;
}

// getters
const getProducts = store.read(products, "products");
const getProductListModules = store.read(productListModules, "productListModules");
const getPropertyGroups = store.read(configuratorPropertyGroups, "configuratorPropertyGroups");
const getApiUrl = store.read(apiUrl, "apiUrl");
const getTranslations = store.read(translations, "translations");
const getConfiguratorValues = store.read(configuratorValues, "configuratorValues");
const getPropertyModelValues = store.read(propertyModelValues, "propertyModelValues");
const getConfiguratorType = store.read(configuratorType, "configuratorType");
const getStepByStepInstructionsProducts = store.read(stepByStepInstructionsProducts, "stepByStepInstructionsProducts");

// mutations
function commitAddOrUpdateProduct(state: ProductConfiguratorState, payload: Models.Product) {
        const tempProducts = [...state.products];
        const productIndex = state.products.findIndex(p => p.articleNumber === payload.articleNumber);

        if (productIndex === -1) {
            tempProducts.push(Object.assign({}, payload));
        } else {
            tempProducts[productIndex] = Object.assign({}, payload);
        }

        state.products = tempProducts;
}

function commitRemoveProduct(state: ProductConfiguratorState, payload: Models.CoreProduct)  {
    const tempProducts = [...state.products];
    const productIndex = tempProducts.findIndex(p => p.articleNumber === payload.articleNumber);

    if (productIndex !== -1) {
        tempProducts.splice(productIndex, 1);
    }

    state.products = tempProducts;
}

function commitStepByStepInstructionsAddProduct(state: ProductConfiguratorState, payload: Models.Product) {
    const tempProducts = [...state.stepByStepInstructionsProducts];
    const productIndex = state.stepByStepInstructionsProducts.findIndex(p => p.articleNumber === payload.articleNumber);

    if (productIndex === -1) {
        tempProducts.push(Object.assign({}, payload));
    } else {
        tempProducts[productIndex] = Object.assign({}, payload);
    }

    state.stepByStepInstructionsProducts = tempProducts;
}

function commitStepByStepInstructionsRemoveProduct(state: ProductConfiguratorState, payload: Models.CoreProduct)  {
const tempProducts = [...state.stepByStepInstructionsProducts];
const productIndex = tempProducts.findIndex(p => p.articleNumber === payload.articleNumber);

if (productIndex !== -1) {
    tempProducts.splice(productIndex, 1);
}

state.stepByStepInstructionsProducts = tempProducts;
}

function commitAddProductListModule(state: ProductConfiguratorState, payload: Models.ProductListModuleModel) {
    const tempProductsListModules = [...state.productListModules];
    const productListModuleIndex = tempProductsListModules.findIndex(p => p.id === payload.id);

    if (productListModuleIndex === -1) {
        tempProductsListModules.push(Object.assign({}, payload));
    } else {
        tempProductsListModules[productListModuleIndex] = Object.assign({}, payload);
    }

    state.productListModules = tempProductsListModules;
}

function commitAddConfiguratorValue(state: ProductConfiguratorState, payload: Models.ConfiguratorModuleValue)  {
    const tempConfiguratorValues = [...state.configuratorValues];
    tempConfiguratorValues.push(Object.assign({}, payload));
    state.configuratorValues = tempConfiguratorValues;
}

function commitAddPropertyGroup(state: ProductConfiguratorState, payload: Models.ConfiguratorPropertyGroup)  {
    state.propertyGroups.push(payload);
}

function commitAddPropertyGroups(state: ProductConfiguratorState, payload: Models.ConfiguratorPropertyGroup[])  {
    payload.forEach(propertyGroup => commitAddPropertyGroup(state, propertyGroup));
}

function commitUpdateConfiguratorValue(state: ProductConfiguratorState, payload: Models.ConfiguratorValueUpdateModel)  {
    const tempConfiguratorValues = [...state.configuratorValues];

    const configuratorValue = tempConfiguratorValues.find(p => {
        if (payload.propertyName) {
            return (payload.propertyName === configuratorConstants.recommendedProductSelected
                || configuratorConstants.ResetPropertiesOnFloorChange.indexOf(payload.propertyName + ";") > -1) ? p.property.propertyName === payload.propertyName
            : p.id === payload.moduleId && p.property.propertyName === payload.propertyName;
        }
        return p.id === payload.moduleId;
    });
    if (configuratorValue) {
        configuratorValue.value = payload.value;
        configuratorValue.valueDescriptions = payload.valueDescriptions;
    }

    // for basic elements configurator, has to be changed when another configurator uses this
    if (state.configuratorType === configuratorConstants.BasicElementsProductSelector) {
        if ((payload.propertyName === configuratorConstants.OuterCorners || payload.propertyName === configuratorConstants.InnerCorners) && payload.value) {
            const relatedValue = tempConfiguratorValues.find(a => a && a.property.propertyName === configuratorConstants.GroundShape && a.value);
            if (relatedValue) {
                relatedValue.value = undefined;
            }
        } else if (payload.propertyName === configuratorConstants.GroundShape && payload.value) {
            const relatedValue = tempConfiguratorValues.find(a => a.property.propertyName === configuratorConstants.OuterCorners);
            if (relatedValue) {
                relatedValue.value = "";
            }
            const relatedValueInner = tempConfiguratorValues.find(a => a.property.propertyName === configuratorConstants.InnerCorners);
            if (relatedValueInner) {
                relatedValueInner.value = "";
            }
        }

        if (payload.propertyName === configuratorConstants.PlasticBoardPercentage ||
            payload.propertyName === configuratorConstants.SecurityMargin) {
            UpdateConfiguratorFieldValue(tempConfiguratorValues, payload, payload.propertyName);
        }
    }

    if (state.configuratorType === configuratorConstants.BuildingProductSelector) {
        if (payload.propertyName === configuratorConstants.MaterialConsumptionFactorOutdoorWall ||
            payload.propertyName === configuratorConstants.MaterialConsumptionFactorIndoorWall ||
            payload.propertyName === configuratorConstants.MaterialConsumptionFactorExaktOutdoorWall ||
            payload.propertyName === configuratorConstants.CornerBlockOpeningBlockQuantityFactor ||
            payload.propertyName === configuratorConstants.AdjustmentFactorForNormalBlockPassPicker) {
            UpdateConfiguratorFieldValue(tempConfiguratorValues, payload, payload.propertyName);
        }
    }

    if (state.configuratorType === configuratorConstants.GreenhouseProductSelector) {
        if (payload.propertyName === configuratorConstants.FloorType) {
            configuratorConstants.ResetPropertiesOnFloorChange.split(";").map(propertyName => {
                const configuratorValueUpdate = tempConfiguratorValues.find(p => p.property.propertyName === propertyName);
                if (configuratorValueUpdate) {
                    configuratorValueUpdate.value = undefined;
                    configuratorValueUpdate.valueDescriptions = [{ title: "", text: "" }];
                }
            });
        }
    }
    state.configuratorValues = tempConfiguratorValues;
}

function UpdateConfiguratorFieldValue(tempConfiguratorValues: Models.ConfiguratorModuleValue[], payload: Models.ConfiguratorValueUpdateModel, propertyName: string) {
    const existingValue = tempConfiguratorValues.find(a => a.property.propertyName === propertyName);
    if (existingValue && payload.value && existingValue.value !== payload.value) {
        existingValue.value = payload.value;
        existingValue.valueDescriptions = payload.valueDescriptions;
    }
}

function commitAddPropertyModelValue(state: ProductConfiguratorState, payload: Models.PropertyModelValue) {
    const tempPropertyModelValues = [...state.propertyModelValues];
    const valueIndex = tempPropertyModelValues.findIndex(p => p.key === payload.key);

    if (valueIndex === -1) {
        tempPropertyModelValues.push(Object.assign({}, payload));
    } else {
        tempPropertyModelValues[valueIndex] = Object.assign({}, payload);
    }

    state.propertyModelValues = tempPropertyModelValues;
}

function commitSetConfiguratorType(state: ProductConfiguratorState, payload: string) {
    state.configuratorType = payload;
}

// actions
async function addProduct(context: BareActionContext<ProductConfiguratorState, RootState>, product: Models.Product) {
    commitAddOrUpdateProduct(context.state, product);
}

async function updateProduct(context: BareActionContext<ProductConfiguratorState, RootState>, product: Models.Product) {
    commitAddOrUpdateProduct(context.state, product);
}

async function removeProduct(context: BareActionContext<ProductConfiguratorState, RootState>, product: Models.CoreProduct) {
    commitRemoveProduct(context.state, product);
}

async function stepByStepInstructionsAddProduct(context: BareActionContext<ProductConfiguratorState, RootState>, product: Models.Product) {
    commitStepByStepInstructionsAddProduct(context.state, product);
}


async function stepByStepInstructionsRemoveProduct(context: BareActionContext<ProductConfiguratorState, RootState>, product: Models.CoreProduct) {
    commitStepByStepInstructionsRemoveProduct(context.state, product);
}

async function addProductListModule(context: BareActionContext<ProductConfiguratorState, RootState>, productListModule: Models.ProductListModuleModel) {
    commitAddProductListModule(context.state, productListModule);
}

async function addConfiguratorValue(context: BareActionContext<ProductConfiguratorState, RootState>, configuratorValue: Models.ConfiguratorModuleValue) {
    commitAddConfiguratorValue(context.state, configuratorValue);
}

async function addPropertyGroup(context: BareActionContext<ProductConfiguratorState, RootState>, propertyGroup: Models.ConfiguratorPropertyGroup) {
    commitAddPropertyGroup(context.state, propertyGroup);
}

async function addPropertyGroups(context: BareActionContext<ProductConfiguratorState, RootState>, propertyGroups: Models.ConfiguratorPropertyGroup[]) {
    commitAddPropertyGroups(context.state, propertyGroups);
}

async function updateConfiguratorValue(context: BareActionContext<ProductConfiguratorState, RootState>, model: Models.ConfiguratorValueUpdateModel) {
    commitUpdateConfiguratorValue(context.state, model);
}

async function addPropertyModelValue(context: BareActionContext<ProductConfiguratorState, RootState>, model: Models.PropertyModelValue) {
    commitAddPropertyModelValue(context.state, model);
}

async function setConfiguratorType(context: BareActionContext<ProductConfiguratorState, RootState>, model: string) {
    commitSetConfiguratorType(context.state, model);
}

export const productConfiguratorStore = {
    get products() { return getProducts(); },
    get productListModules() { return getProductListModules(); },
    get configuratorValues() { return getConfiguratorValues(); },
    get propertyModelValues() { return getPropertyModelValues(); },
    get propertyGroups() { return getPropertyGroups(); },
    get apiUrl() { return getApiUrl(); },
    get translations() { return getTranslations(); },
    get configuratorType() { return getConfiguratorType(); },
    get stepByStepInstructionsProducts() { return getStepByStepInstructionsProducts(); },
    addProduct: store.dispatch(addProduct, "addProduct"),
    updateProduct: store.dispatch(updateProduct, "updateProduct"),
    removeProduct: store.dispatch(removeProduct, "removeProduct"),
    addProductListModule: store.dispatch(addProductListModule, "addProductListModule"),
    addConfiguratorValue: store.dispatch(addConfiguratorValue, "addConfiguratorValue"),
    addPropertyModelValue: store.dispatch(addPropertyModelValue, "addPropertyModelValue"),
    addPropertyGroup: store.dispatch(addPropertyGroup, "addProductGroup"),
    addPropertyGroups: store.dispatch(addPropertyGroups, "addProductGroups"),
    updateConfiguratorValue: store.dispatch(updateConfiguratorValue, "updateConfiguratorValue"),
    setConfiguratorType: store.dispatch(setConfiguratorType, "setConfiguratorType"),
    stepByStepInstructionsAddProduct: store.dispatch(stepByStepInstructionsAddProduct, "stepByStepInstructionsAddProduct"),
    stepByStepInstructionsRemoveProduct: store.dispatch(stepByStepInstructionsRemoveProduct, "stepByStepInstructionsRemoveProduct"),
};
